<template>
  <div class="user-growth-stats">
    <div class="page-header">
      <h2>用户增长趋势</h2>
      <div class="header-controls">
        <div class="date-search">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :shortcuts="dateShortcuts"
          />
          <el-button type="primary" :icon="Search" @click="handleSearch">搜索</el-button>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <el-row :gutter="20">
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>总用户数</span>
                <el-icon><User /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ stats.totalUsers }}</div>
              <div class="stat-label">选定日期累计注册用户</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>选定日期用户数</span>
                <el-icon><UserFilled /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ stats.newUsers }}</div>
              <div class="stat-label">选定日期新增用户数</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>当日增长数</span>
                <el-icon><TrendCharts /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ stats.todayGrowth }}</div>
              <div class="stat-label">今日新增用户数</div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 用户增长趋势图表 -->
    <div class="chart-container">
      <el-card class="chart-card">
        <template #header>
          <div class="card-header">
            <span>用户增长趋势</span>
          </div>
        </template>
        <div class="chart-content">
          <ChartBox
            :chart-data="growthChartData"
            :chart-options="growthChartOptions"
            height="400px"
          />
        </div>
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import ChartBox from '@/components/admin/ChartBox.vue'
import { User, UserFilled, TrendCharts, Search } from '@element-plus/icons-vue'
import request from '@/utils/request'

// 日期范围选择
const dateRange = ref([])
const dateShortcuts = [
  {
    text: '最近一周',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
      return [start, end]
    }
  },
  {
    text: '最近一个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
      return [start, end]
    }
  },
  {
    text: '最近三个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
      return [start, end]
    }
  }
]

// 统计数据
const stats = ref({
  totalUsers: 0,
  newUsers: 0,
  todayGrowth: 0
})

// 图表数据
const growthChartData = ref({
  labels: [],
  datasets: [
    {
      label: '新增用户',
      data: [],
      backgroundColor: 'rgba(64, 158, 255, 0.2)',
      borderColor: 'rgba(64, 158, 255, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    },
    {
      label: '累计用户',
      data: [],
      backgroundColor: 'rgba(103, 194, 58, 0.2)',
      borderColor: 'rgba(103, 194, 58, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

const growthChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          const label = context.dataset.label || ''
          return `${label}: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      },
      ticks: {
        maxRotation: 45,
        minRotation: 45
      }
    }
  }
}

// 获取统计数据
const fetchStats = async () => {
  try {
    const response = await request.get('/admin/stats/users', {
      params: {
        startDate: dateRange.value[0]?.toISOString().split('T')[0],
        endDate: dateRange.value[1]?.toISOString().split('T')[0]
      }
    })
    
    if (!response || !response.trend) {
      throw new Error('返回数据格式不正确')
    }
    
    // 直接使用后端返回的统计数据
    stats.value = {
      totalUsers: response.stats.totalUsers,
      newUsers: response.stats.newUsers,
      todayGrowth: response.stats.todayGrowth
    }
    
    // 更新图表数据
    const trendData = response.trend.map(item => ({
      date: item.date,
      new_users: parseInt(item.newUsers) || 0,
      total_users: parseInt(item.totalUsers) || 0
    }))
    updateChartData(trendData)
  } catch (error) {
    console.error('获取统计数据失败：', error)
    ElMessage.error(error.message || '获取统计数据失败')
  }
}

// 更新图表数据
const updateChartData = (trendData) => {
  if (!trendData || trendData.length === 0) {
    // 如果没有数据，清空图表
    growthChartData.value = {
      labels: [],
      datasets: [
        {
          label: '新增用户',
          data: [],
          backgroundColor: 'rgba(64, 158, 255, 0.2)',
          borderColor: 'rgba(64, 158, 255, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        },
        {
          label: '累计用户',
          data: [],
          backgroundColor: 'rgba(103, 194, 58, 0.2)',
          borderColor: 'rgba(103, 194, 58, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    }
    return
  }

  // 格式化日期标签
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return `${date.getMonth() + 1}/${date.getDate()}`
  }

  // 更新图表数据
  growthChartData.value = {
    labels: trendData.map(item => formatDate(item.date)),
    datasets: [
      {
        label: '新增用户',
        data: trendData.map(item => item.new_users),
        backgroundColor: 'rgba(64, 158, 255, 0.2)',
        borderColor: 'rgba(64, 158, 255, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true
      },
      {
        label: '累计用户',
        data: trendData.map(item => item.total_users),
        backgroundColor: 'rgba(103, 194, 58, 0.2)',
        borderColor: 'rgba(103, 194, 58, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true
      }
    ]
  }

  // 更新图表配置
  const maxNewUsers = Math.max(...trendData.map(item => item.new_users))
  const maxTotalUsers = Math.max(...trendData.map(item => item.total_users))
  growthChartOptions.scales.y.ticks.stepSize = Math.ceil(Math.max(maxNewUsers, maxTotalUsers) / 5)
}

// 搜索处理
const handleSearch = () => {
  if (!dateRange.value || dateRange.value.length !== 2) {
    ElMessage.warning('请选择日期范围')
    return
  }
  fetchStats()
}

// 初始化
onMounted(() => {
  // 设置默认日期范围为最近一个月
  dateRange.value = dateShortcuts[1].value()
  // 初始化时加载数据
  fetchStats()
})
</script>

<style scoped>
.user-growth-stats {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.stats-cards {
  margin-bottom: 20px;
}

.stat-card {
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-content {
  text-align: center;
  padding: 20px 0;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 8px;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.chart-container {
  margin-bottom: 20px;
}

.chart-content {
  height: 400px;
}

.date-search {
  display: flex;
  gap: 12px;
  align-items: center;
}

.header-controls {
  display: flex;
  gap: 16px;
  align-items: center;
}
</style> 